// ============================================================================
// INVENTORY CORE TYPES (ADR-0058 slice 1) — aligned with Fbp.Core.DTOs.Inventory.
// Item categories, classification dimensions, stock locations, inventory
// settings (policy, default ledgers, ledger overrides) and the item master.
// Items, categories and classifications are enterprise-shared (ADR-0028);
// locations and settings are per entity (X-Entity-ID).
// ============================================================================

/** Paging + search every inventory list endpoint binds (Fbp.Shared.Models.QueryParameters). */
interface QueryParams {
  page?: number;
  pageSize?: number;
  searchTerm?: string;
  sortBy?: string;
  sortDescending?: boolean;
}

/** Shared audit + status tail of the inventory read models. */
interface InventoryAudit {
  statusId?: string | null;
  statusCode?: string | null;
  statusName?: string | null;
  statusColor?: string | null;
  rowVersion: number;
  createdAt?: string;
  createdByName?: string | null;
  updatedAt?: string | null;
  updatedByName?: string | null;
}

// ---------------------------------------------------------------------------
// Item categories
// ---------------------------------------------------------------------------

export interface ItemCategoryDto extends InventoryAudit {
  id: string;
  entityId?: string | null;
  categoryCode: string;
  categoryName: string;
  parentId?: string | null;
  parentName?: string | null;
  /** Full path from the root, e.g. "Non-Foods / Household". */
  path?: string | null;
  displayOrder: number;
  description?: string | null;
  itemCount: number;
  childCount: number;
  /** Ledger overrides any entity has on this category (delete is refused while > 0). */
  ledgerOverrideCount: number;
}

export interface CreateItemCategoryRequest {
  categoryCode: string;
  categoryName: string;
  parentId?: string | null;
  displayOrder: number;
  description?: string | null;
  statusCode: string;
}

export interface UpdateItemCategoryRequest extends CreateItemCategoryRequest {
  rowVersion: number;
}

export interface ItemCategoryQueryParams extends QueryParams {
  parentId?: string;
  rootsOnly?: boolean;
  includeInactive?: boolean;
}

// ---------------------------------------------------------------------------
// Classification dimensions
// ---------------------------------------------------------------------------

export interface ClassificationValueDto {
  id: string;
  dimensionId: string;
  valueCode: string;
  valueName: string;
  parentId?: string | null;
  parentCode?: string | null;
  parentName?: string | null;
  displayOrder: number;
  /** Items carrying this value; removing it is refused while > 0. */
  itemCount: number;
  /** Child values in the same classification (0 = a leaf). */
  childCount: number;
  /** Values of dependent classifications allowed under this one; removing it is refused while > 0. */
  dependentLinkCount: number;
  /** Parent-classification values this value is allowed under; empty = any. */
  allowedUnder: ClassificationValueRefDto[];
}

export interface ClassificationValueRefDto {
  valueId: string;
  valueCode: string;
  valueName: string;
}

export interface ClassificationDimensionDto extends InventoryAudit {
  id: string;
  entityId?: string | null;
  dimensionCode: string;
  dimensionName: string;
  isHierarchical: boolean;
  isRequired: boolean;
  /** Hierarchical only: items take only values with no children. */
  leafOnly: boolean;
  /** The classification this one depends on (Sub-category → Category); null = independent. */
  dependsOnDimensionId?: string | null;
  dependsOnDimensionName?: string | null;
  /** Null = managed here; a module code = maintained by that module (read-only on the core screen). */
  ownerModule?: string | null;
  displayOrder: number;
  description?: string | null;
  /** Other classifications depending on this one (delete is refused while > 0). */
  dependentDimensionCount: number;
  valueCount: number;
  /** Filled on detail reads only. */
  values: ClassificationValueDto[];
}

/** One value in a dimension save. id = existing row; parent named by code so a new child can point at a new parent. */
export interface ClassificationValueInput {
  id?: string | null;
  valueCode: string;
  valueName: string;
  parentCode?: string | null;
  /** Dependent classifications only: parent-classification value ids this value is allowed under; empty = any. */
  allowedUnderValueIds: string[];
  displayOrder: number;
}

export interface CreateClassificationDimensionRequest {
  dimensionCode: string;
  dimensionName: string;
  isHierarchical: boolean;
  isRequired: boolean;
  leafOnly: boolean;
  dependsOnDimensionId?: string | null;
  displayOrder: number;
  description?: string | null;
  statusCode: string;
  /** The full value set: values left out of an update are removed. */
  values: ClassificationValueInput[];
}

export interface UpdateClassificationDimensionRequest extends CreateClassificationDimensionRequest {
  rowVersion: number;
}

export interface ClassificationDimensionQueryParams extends QueryParams {
  includeInactive?: boolean;
}

export interface ClassificationValueQueryParams extends QueryParams {
  dimensionId: string;
  /** Dependent classification: only values allowed under this parent value (or under any). */
  allowedUnderValueId?: string;
  /** Only values with no children (leaf-only classifications). */
  leavesOnly?: boolean;
}

// ---------------------------------------------------------------------------
// Stock locations
// ---------------------------------------------------------------------------

export type StockLocationType = 'STORAGE' | 'IN_TRANSIT';

export interface StockLocationDto extends InventoryAudit {
  id: string;
  entityId: string;
  businessUnitId: string;
  businessUnitCode?: string | null;
  businessUnitName?: string | null;
  locationCode: string;
  locationName: string;
  locationType: StockLocationType;
  isSaleable: boolean;
  /** The auto-provisioned transit location: not editable or deletable. */
  isSystem: boolean;
  address?: string | null;
  description?: string | null;
  /** Items using this as default location (delete is refused while > 0). */
  defaultForItemCount: number;
}

export interface CreateStockLocationRequest {
  businessUnitId: string;
  locationCode: string;
  locationName: string;
  isSaleable: boolean;
  address?: string | null;
  description?: string | null;
  statusCode: string;
}

export interface UpdateStockLocationRequest extends CreateStockLocationRequest {
  rowVersion: number;
}

export interface StockLocationQueryParams extends QueryParams {
  businessUnitId?: string;
  locationType?: StockLocationType;
  saleableOnly?: boolean;
  includeInactive?: boolean;
}

// ---------------------------------------------------------------------------
// Inventory settings
// ---------------------------------------------------------------------------

export type InventoryAccountingMode = 'NONE' | 'PERIODIC' | 'PERPETUAL';
export type NegativeStockPolicy = 'BLOCK' | 'WARN';

export const INVENTORY_ACCOUNTING_MODES: { value: InventoryAccountingMode; label: string; hint: string }[] = [
  { value: 'PERPETUAL', label: 'Perpetual', hint: 'Stock is an asset as it arrives; cost of goods sold is booked at each sale.' },
  { value: 'PERIODIC', label: 'Periodic', hint: 'Purchases are expensed; closing stock is valued and booked at each month end.' },
  { value: 'NONE', label: 'Quantity only', hint: 'Stock quantities are tracked; nothing is posted to the books.' },
];

export interface InventorySettingDto {
  id: string;
  entityId: string;
  /** A financial-year start (date-only ISO). */
  effectiveFrom: string;
  financialYearName?: string | null;
  accountingMode: InventoryAccountingMode;
  costingMethod: 'WEIGHTED_AVERAGE';
  negativeStockPolicy: NegativeStockPolicy;
  closingStockFrequency: 'MONTHLY';
  rowVersion: number;
  updatedAt?: string | null;
  updatedByName?: string | null;
}

export interface InventoryFinancialYearDto {
  id: string;
  fyCode: string;
  fyName: string;
  startDate: string;
  endDate: string;
}

export interface InventoryLedgerDefaultDto {
  purposeCode: string;
  purposeName: string;
  /** Accounting modes that post to this purpose. */
  usedInModes: InventoryAccountingMode[];
  /** Required under the mode in force today. */
  required: boolean;
  ledgerId?: string | null;
  ledgerCode?: string | null;
  ledgerName?: string | null;
}

export interface InventorySettingsOverviewDto {
  /** The row in force today; null until the first policy is saved. */
  current?: InventorySettingDto | null;
  history: InventorySettingDto[];
  /** Pre-selected mode for a first save (seeded default for the entity's country). */
  suggestedAccountingMode: InventoryAccountingMode;
  countryCode?: string | null;
  financialYears: InventoryFinancialYearDto[];
  ledgerDefaults: InventoryLedgerDefaultDto[];
  transitLocation?: StockLocationDto | null;
}

export interface SaveInventorySettingRequest {
  effectiveFrom: string;
  accountingMode: InventoryAccountingMode;
  costingMethod: 'WEIGHTED_AVERAGE';
  negativeStockPolicy: NegativeStockPolicy;
  closingStockFrequency: 'MONTHLY';
  /** Echo when replacing the row that starts on effectiveFrom; omit for a new row. */
  rowVersion?: number | null;
}

export interface SetInventoryLedgerDefaultRequest {
  purposeCode: string;
  ledgerId: string;
}

export interface LedgerOverrideDto {
  id: string;
  itemId?: string | null;
  itemCode?: string | null;
  itemName?: string | null;
  categoryId?: string | null;
  categoryCode?: string | null;
  categoryName?: string | null;
  purposeCode: string;
  purposeName?: string | null;
  ledgerId: string;
  ledgerCode?: string | null;
  ledgerName?: string | null;
  rowVersion: number;
  updatedAt?: string | null;
  updatedByName?: string | null;
}

/** Exactly one of itemId / categoryId. */
export interface SaveLedgerOverrideRequest {
  itemId?: string | null;
  categoryId?: string | null;
  purposeCode: string;
  ledgerId: string;
}

export interface LedgerOverrideQueryParams extends QueryParams {
  itemId?: string;
  categoryId?: string;
  purposeCode?: string;
  targetType?: 'ITEM' | 'CATEGORY';
}

// ---------------------------------------------------------------------------
// Items
// ---------------------------------------------------------------------------

export type ItemType = 'STOCK' | 'NON_STOCK';
export type IdentifierType = 'GTIN' | 'EAN' | 'UPC' | 'SUPPLIER_CODE' | 'ALIAS';
export const IDENTIFIER_TYPES: { value: IdentifierType; label: string }[] = [
  { value: 'GTIN', label: 'GTIN barcode' },
  { value: 'EAN', label: 'EAN barcode' },
  { value: 'UPC', label: 'UPC barcode' },
  { value: 'SUPPLIER_CODE', label: 'Supplier code' },
  { value: 'ALIAS', label: 'Alias' },
];

export interface ItemUomDto {
  id: string;
  uomId: string;
  uomCode?: string | null;
  uomName?: string | null;
  factorToBase: number;
  isPurchaseDefault: boolean;
  isSalesDefault: boolean;
  netWeight?: number | null;
  grossWeight?: number | null;
  weightUomId?: string | null;
  weightUomCode?: string | null;
  length?: number | null;
  width?: number | null;
  height?: number | null;
  dimensionUomId?: string | null;
  dimensionUomCode?: string | null;
  displayOrder: number;
  /** The base unit's own row (factor 1). */
  isBase: boolean;
}

export interface ItemIdentifierDto {
  id: string;
  itemUomId?: string | null;
  /** The pack's unit; null = the base unit. */
  uomId?: string | null;
  uomCode?: string | null;
  identifierType: IdentifierType;
  identifierValue: string;
  isPrimary: boolean;
}

export interface ItemClassificationDto {
  dimensionId: string;
  dimensionCode?: string | null;
  dimensionName?: string | null;
  valueId: string;
  valueCode?: string | null;
  valueName?: string | null;
}

export interface ItemEntitySettingDto {
  defaultStockLocationId?: string | null;
  defaultStockLocationCode?: string | null;
  defaultStockLocationName?: string | null;
  reorderLevel?: number | null;
  minStock?: number | null;
  maxStock?: number | null;
}

export interface ItemDto extends InventoryAudit {
  id: string;
  entityId?: string | null;
  itemCode: string;
  itemName: string;
  description?: string | null;
  itemType: ItemType;
  baseUomId: string;
  baseUomCode?: string | null;
  baseUomName?: string | null;
  baseUomDecimalPlaces: number;
  categoryId?: string | null;
  categoryCode?: string | null;
  categoryName?: string | null;
  trackBatch: boolean;
  trackExpiry: boolean;
  trackSerial: boolean;
  shelfLifeDays?: number | null;
  blockedForPurchase: boolean;
  blockedForSale: boolean;
  /** List summary, e.g. "CASE ×12, PALLET ×1200". */
  packSummary?: string | null;
  /** List summary: a barcode of the item. */
  primaryBarcode?: string | null;
  /** Detail reads only. */
  uoms: ItemUomDto[];
  identifiers: ItemIdentifierDto[];
  classifications: ItemClassificationDto[];
  entitySetting?: ItemEntitySettingDto | null;
  /** Domain-module payloads keyed by module code (IItemExtension). */
  extensions: Record<string, unknown>;
}

export interface ItemUomInput {
  uomId: string;
  factorToBase: number;
  isPurchaseDefault: boolean;
  isSalesDefault: boolean;
  netWeight?: number | null;
  grossWeight?: number | null;
  weightUomId?: string | null;
  length?: number | null;
  width?: number | null;
  height?: number | null;
  dimensionUomId?: string | null;
  displayOrder: number;
}

export interface ItemIdentifierInput {
  /** The pack this scans to, by unit; null = the base unit. */
  uomId?: string | null;
  identifierType: IdentifierType;
  identifierValue: string;
  isPrimary: boolean;
}

export interface ItemClassificationInput {
  dimensionId: string;
  valueId: string;
}

export interface ItemEntitySettingInput {
  defaultStockLocationId?: string | null;
  reorderLevel?: number | null;
  minStock?: number | null;
  maxStock?: number | null;
}

export interface CreateItemRequest {
  itemCode: string;
  itemName: string;
  description?: string | null;
  itemType: ItemType;
  baseUomId: string;
  categoryId?: string | null;
  trackBatch: boolean;
  trackExpiry: boolean;
  shelfLifeDays?: number | null;
  blockedForPurchase: boolean;
  blockedForSale: boolean;
  statusCode: string;
  /** Full pack set; the base unit's row is added server-side when missing. */
  uoms: ItemUomInput[];
  /** Full identifier set (replaces the stored one). */
  identifiers: ItemIdentifierInput[];
  /** Full classification set (replaces the stored one). */
  classifications: ItemClassificationInput[];
  /** This entity's stocking settings; null = leave unchanged. */
  entitySetting?: ItemEntitySettingInput | null;
  /** Domain-module payloads; omitted modules are left as they are. */
  extensions?: Record<string, unknown> | null;
}

export interface UpdateItemRequest extends CreateItemRequest {
  rowVersion: number;
}

export interface ItemQueryParams extends QueryParams {
  categoryId?: string;
  includeSubCategories?: boolean;
  itemType?: ItemType;
  classificationValueId?: string;
  /** Exact identifier match (scanner input). */
  identifier?: string;
  blockedForSale?: boolean;
  blockedForPurchase?: boolean;
  includeInactive?: boolean;
}

// ---------------------------------------------------------------------------
// Stock ledger reports (ADR-0058 slice 2)
// ---------------------------------------------------------------------------

export interface StockPositionRowDto {
  itemId: string;
  itemCode: string;
  itemName: string;
  baseUomCode?: string | null;
  categoryName?: string | null;
  /** Null when grouped by item (all locations together). */
  stockLocationId?: string | null;
  locationCode?: string | null;
  locationName?: string | null;
  businessUnitName?: string | null;
  quantity: number;
  value: number;
  /** value ÷ quantity; null when the quantity is zero. */
  averageCost?: number | null;
  reorderLevel?: number | null;
  /** Sum of value over every row of the report (not just this page). */
  grandTotalValue: number;
}

export interface StockPositionQueryParams extends QueryParams {
  /** As at the end of this date (yyyy-MM-dd); default today. */
  asOf?: string;
  groupBy?: 'ITEM_LOCATION' | 'ITEM';
  stockLocationId?: string;
  businessUnitId?: string;
  categoryId?: string;
  classificationValueId?: string;
  includeZero?: boolean;
}

export interface StockCardLineDto {
  movementId: string;
  movementDate: string;
  movementType: string;
  sourceModule: string;
  sourceDocumentId: string;
  sourceReference?: string | null;
  locationCode?: string | null;
  inQuantity: number;
  outQuantity: number;
  unitCost: number;
  value: number;
  costSource: 'ENTERED' | 'AVERAGE' | 'LINKED';
  runningQuantity: number;
  runningValue: number;
  narration?: string | null;
}

export interface StockCardDto {
  itemId: string;
  itemCode: string;
  itemName: string;
  baseUomCode?: string | null;
  locationName?: string | null;
  fromDate: string;
  toDate: string;
  openingQuantity: number;
  openingValue: number;
  closingQuantity: number;
  closingValue: number;
  lines: StockCardLineDto[];
}

export interface StockCardQueryParams {
  itemId: string;
  stockLocationId?: string;
  fromDate?: string;
  toDate?: string;
}

/** Readable names of movement types for registers and the stock card. */
export const MOVEMENT_TYPE_LABELS: Record<string, string> = {
  OPENING: 'Opening stock',
  PURCHASE_RECEIPT: 'Purchase',
  PURCHASE_RETURN: 'Purchase return',
  SALES_ISSUE: 'Sale',
  SALES_RETURN: 'Sales return',
  TRANSFER_ISSUE: 'Transfer out',
  TRANSFER_RECEIPT: 'Transfer in',
  ADJUSTMENT_GAIN: 'Adjustment (gain)',
  ADJUSTMENT_LOSS: 'Adjustment (loss)',
  REVALUATION: 'Revaluation',
  COST_CORRECTION: 'Cost correction',
};

// ---------------------------------------------------------------------------
// Opening Stock document (stock operations, ADR-0058 §9)
// ---------------------------------------------------------------------------

export interface OpeningStockLineDto {
  id: string;
  lineNo: number;
  itemId: string;
  itemCode?: string | null;
  itemName?: string | null;
  stockLocationId: string;
  locationCode?: string | null;
  locationName?: string | null;
  uomId: string;
  uomCode?: string | null;
  quantity: number;
  factorToBase: number;
  baseQuantity: number;
  baseUomCode?: string | null;
  unitCost: number;
  value: number;
  narration?: string | null;
}

export interface OpeningStockDto {
  id: string;
  entityId: string;
  businessUnitId: string;
  businessUnitName?: string | null;
  documentNumber: string;
  documentDate: string;
  reference?: string | null;
  narration?: string | null;
  totalValue: number;
  docStatus: 'POSTED' | 'CANCELLED';
  lineCount: number;
  /** The Stock Valuation journal's number; null under quantity-only or when cancelled. */
  valuationTransactionNo?: string | null;
  lines: OpeningStockLineDto[];
  /** Negative-stock warnings from the last save (WARN policy). */
  warnings: string[];
  rowVersion: number;
  createdAt?: string;
  createdByName?: string | null;
  updatedAt?: string | null;
  updatedByName?: string | null;
}

export interface OpeningStockLineInput {
  itemId: string;
  stockLocationId: string;
  /** One of the item's packs (its base unit included). */
  uomId: string;
  quantity: number;
  /** Cost of one uomId unit. */
  unitCost: number;
  narration?: string | null;
}

export interface SaveOpeningStockRequest {
  documentDate: string;
  reference?: string | null;
  narration?: string | null;
  lines: OpeningStockLineInput[];
  /** Echo on update. */
  rowVersion?: number | null;
}

export interface OpeningStockQueryParams extends QueryParams {
  fromDate?: string;
  toDate?: string;
  includeCancelled?: boolean;
}
